import type { ReactNode } from 'react';
import React, { useCallback } from 'react';

import { ClickAwayListener, IconButton, Portal } from '@mui/material';
import { CloseIcon } from 'src/components/icons/CloseIcon';
import {
  useMobileSheet,
  useMobileSheetKeyboardInset,
  useMobileSheetViewportWidth,
} from 'src/editor/mobile/use-mobile-sheet';
import { makeStyles } from 'tss-react/mui';

/**
 * Marker class to apply to portal'd descendants (e.g. an Autocomplete listbox
 * Popper) so MobileBottomSheet's ClickAwayListener can recognise taps that
 * land inside them as belonging to the sheet and skip its dismiss.
 */
export const MOBILE_SHEET_PORTAL_CLASS = 'aline-mobile-sheet-portal';

const useStyles = makeStyles()(theme => ({
  closeButton: {
    padding: theme.spacing(1),
  },
  closeIcon: {
    height: 20,
    width: 20,
  },
  /**
   * Pulls the button to the sheet's top-right corner without expanding the
   * sheet's outer padding; body content stays on its own row below.
   */
  closeRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(0.5),
    marginRight: theme.spacing(-1),
    marginTop: theme.spacing(-0.5),
  },
  sheet: {
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: theme.spacing(2),
    borderTopRightRadius: theme.spacing(2),
    bottom: theme.spacing(0),
    boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.18)',
    boxSizing: 'border-box',
    left: 0,
    maxHeight: '92vh',
    overflowY: 'auto',
    padding: theme.spacing(1.5, 2, 2.5),
    position: 'fixed',
    transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: theme.zIndex.modal,
  },
}));

interface MobileBottomSheetProps {
  children: ReactNode;
  onDismiss: () => void;
}

export function MobileBottomSheet({ children, onDismiss }: MobileBottomSheetProps): JSX.Element {
  const { shown } = useMobileSheet();
  const { classes } = useStyles();
  const viewportWidth = useMobileSheetViewportWidth();
  const keyboardInsetPx = useMobileSheetKeyboardInset();

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(`.${MOBILE_SHEET_PORTAL_CLASS}`)) return;
      onDismiss();
    },
    [onDismiss]
  );

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          className={classes.sheet}
          style={{
            bottom: keyboardInsetPx,
            transform: shown ? 'translateY(0)' : 'translateY(calc(100% + 120px))',
            width: viewportWidth,
            ...(keyboardInsetPx > 0
              ? { maxHeight: `calc(100vh - ${keyboardInsetPx + 16}px)` }
              : {}),
          }}
        >
          <div className={classes.closeRow}>
            <IconButton
              aria-label="Close"
              className={classes.closeButton}
              size="small"
              onClick={onDismiss}
            >
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
          {children}
        </div>
      </ClickAwayListener>
    </Portal>
  );
}
